import { Entity } from '@/lib/realtime/RealtimeEntity';
import {
  EntityInterfaceMap,
  EntityRelationConfig,
  EntityRelationMap,
} from '@/lib/realtime/weak/realtimeTypes';
import { isFuture } from '@/util/dateArithmetic';
import {
  JobRoleFromJSON,
  JobRoleStatusEnum,
  LeaveAllowanceChunkFromJSON,
  LocationFromJSON,
  LocationStatusEnum,
  ScheduleFromJSON,
  ScheduleStatusEnum,
  ShiftAreaSessionFromJSON,
  ShiftConflictFromJSON,
  ShiftTemplateShiftAreaSessionFromJSON,
  TagFromJSON,
  TagStatusEnum,
} from '../../../../api/v1';

/**
 * An entity relationship config to get the entity maker and relationship keys for a related entity,
 * e.g. scheduled breaks to a shift
 */
export const entityRelationConfig: EntityRelationConfig<
  EntityInterfaceMap,
  EntityRelationMap
> = {
  [Entity.JobRole]: {
    entityMaker: JobRoleFromJSON,
    [Entity.Employee]: {
      relationshipKeys: {
        parentToChild: 'jobRoles',
        parentToChildIds: 'jobRoleIds',
      },
      detachHandler: (jobRole) => jobRole.status === JobRoleStatusEnum.Deleted,
    },
  },
  [Entity.Location]: {
    entityMaker: LocationFromJSON,
    [Entity.Employee]: {
      relationshipKeys: {
        parentToChild: 'locations',
        parentToChildIds: 'locationIds',
      },
      detachHandler: (location) =>
        location.status === LocationStatusEnum.Deleted,
    },
    [Entity.ClockInPortal]: {
      relationshipKeys: {
        parentToChildIds: 'locationIds',
      },
      detachHandler: (location) =>
        location.status === LocationStatusEnum.Deleted,
    },
    [Entity.ShiftArea]: {
      relationshipKeys: {
        parentToChild: 'locations',
        parentToChildIds: 'locationIds',
      },
      detachHandler: (location) =>
        location.status === LocationStatusEnum.Deleted,
    },
  },
  [Entity.Schedule]: {
    entityMaker: ScheduleFromJSON,
    [Entity.Employee]: {
      relationshipKeys: {
        parentToChild: 'schedules',
        parentToChildIds: 'scheduleIds',
      },
      detachHandler: (schedule) =>
        schedule.status === ScheduleStatusEnum.Deleted,
    },
  },
  [Entity.ShiftAreaSession]: {
    entityMaker: ShiftAreaSessionFromJSON,
    [Entity.Shift]: {
      relationshipKeys: {
        parentToChild: 'shiftAreaSessions',
        childToParent: 'shiftId',
      },
    },
  },
  [Entity.ShiftConflict]: {
    entityMaker: ShiftConflictFromJSON,
    [Entity.Shift]: {
      relationshipKeys: {
        parentToChild: 'shiftConflicts',
        childToParent: 'shiftId',
      },
    },
  },
  [Entity.ShiftTemplateAreaSession]: {
    entityMaker: ShiftTemplateShiftAreaSessionFromJSON,
    [Entity.ShiftTemplate]: {
      relationshipKeys: {
        parentToChild: 'shiftTemplateAreaSessions',
        childToParent: 'shiftTemplateId',
      },
    },
  },
  [Entity.LeaveAllowanceChunk]: {
    entityMaker: LeaveAllowanceChunkFromJSON,
    [Entity.LeaveRequest]: {
      relationshipKeys: {
        parentToChild: 'leaveAllowanceChunks',
        childToParent: 'leaveRequestId',
      },
    },
  },
  [Entity.Tag]: {
    entityMaker: TagFromJSON,
    [Entity.Shift]: {
      relationshipKeys: {
        parentToChild: 'tags',
        parentToChildIds: 'tagIds',
      },
      detachHandler: (
        tag: EntityRelationMap[typeof Entity.Tag],
        shift: EntityInterfaceMap[typeof Entity.Shift],
      ): boolean =>
        tag.status === TagStatusEnum.Deleted && isFuture(shift.startsAt),
    },
    [Entity.ShiftTemplate]: {
      relationshipKeys: {
        parentToChild: 'tags',
        parentToChildIds: 'tagIds',
      },
      detachHandler: (tag: EntityRelationMap[typeof Entity.Tag], _): boolean =>
        tag.status === TagStatusEnum.Deleted,
    },
  },
};
